/**
 * CRM Capture Utility for AMIVA Business Valuation
 * 
 * Secure Monday.com integration routed through Netlify Function
 */

import { appConfig } from '../config/appConfig';
import logger from './logger';

class CRMCapture {
  constructor() {
    // Monday.com integration is proxied through the Netlify function
    this.captureEndpoint = appConfig.api.monday.captureEndpoint;
    this.isEnabled = this.shouldEnableCapture();

    if (!this.isEnabled) {
      logger.warn('Monday.com CRM capture disabled; missing serverless endpoint or feature flag.');
    }
  }

  /**
   * Determine if CRM capture should run.
   * Can be disabled with REACT_APP_ENABLE_CRM_CAPTURE=false or missing endpoint.
   */
  shouldEnableCapture() {
    const disabledFlag =
      typeof process !== 'undefined' &&
      process.env &&
      process.env.REACT_APP_ENABLE_CRM_CAPTURE === 'false';

    if (disabledFlag) {
      return false;
    }

    if (!this.captureEndpoint) {
      return false;
    }

    return true;
  }

  /**
   * Capture a valuation lead directly to Monday.com
   */
  async captureValuation(valuationData) {
    if (!this.isEnabled) {
      logger.log('Monday.com CRM capture disabled, skipping...');
      return;
    }

    try {
      logger.log('Monday.com CRM Capture starting...');
      
      // Extract UTM parameters from URL
      const urlParams = new URLSearchParams(window.location.search);
      const utmData = {
        utm_source: urlParams.get('utm_source') || 'direct',
        utm_medium: urlParams.get('utm_medium') || 'organic',
        utm_campaign: urlParams.get('utm_campaign') || null
      };

      // Format the lead data for Monday.com
      const leadData = this.formatLeadForMonday({
        ...valuationData,
        ...utmData,
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent,
        referrer: document.referrer || null,
        pageUrl: window.location.href
      });

      logger.log('Sending valuation data to Monday.com:', leadData);

      // Create item via secure Netlify function
      const result = await this.createMondayItem(leadData);
      
      logger.log('Monday.com CRM capture successful:', result);
      
      return result;
    } catch (error) {
      logger.error('Monday.com CRM capture error:', error);
      logger.error('Error details:', error.message, error.stack);
      // Don't throw error - CRM capture should not break the user experience
      return null;
    }
  }

  /**
   * Create item in Monday.com board
   */
  async createMondayItem(leadData) {
    const response = await fetch(this.captureEndpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        itemName: leadData.itemName,
        columnValues: leadData.columnValues,
      }),
    });

    if (!response.ok) {
      const errorText = await response.text().catch(() => '');
      throw new Error(`Monday CRM function error: ${response.status} ${response.statusText} ${errorText}`);
    }

    const result = await response.json().catch(() => null);
    
    if (!result || result.error) {
      const message = result?.error || 'Unknown CRM function error';
      throw new Error(message);
    }

    return result.item;
  }

  /**
   * Format lead data for Monday.com columns - Mimicking backend process exactly
   */
  formatLeadForMonday(data) {
    const companyName = this.extractCompanyName(data.website);
    const valuation = data.valuation || {};
    const leadScore = this.calculateLeadScore(valuation, data.financialInputs);
    
    // Build column values exactly like backend did
    const columnValues = {};
    
    // Set Status Stage to "Intro Call / Qualification"
    columnValues.color_mkvpv4n = 'Intro Call / Qualification';
    
    // Set Stage Entered At to today
    columnValues.date_mkvpkyr3 = { date: new Date().toISOString().split('T')[0] };
    
    // Company Information
    if (companyName) {
      columnValues.text_mkvp36jg = companyName; // Company Legal Name
      columnValues.text_mkvp2dwe = companyName; // Company DBA
    }
    
    if (data.website) {
      columnValues.link_mkvp8sta = { url: data.website, text: companyName || 'Company Website' };
    }
    
    // Industry mapping (same as backend)
    if (data.businessAnalysis?.industry) {
      columnValues.dropdown_mkvpxgmc = this.mapIndustryToDropdown(data.businessAnalysis.industry);
      columnValues.text_mkvpnfwp = data.businessAnalysis.industry; // Keep original detail
    }
    
    // Financial Data from Valuation
    if (valuation.ebitda || valuation.adjustedEBITDA) {
      const ebitda = valuation.ebitda || valuation.adjustedEBITDA || (data.financialInputs?.preTaxProfit * 1.1);
      columnValues.numeric_mkvpf12h = Math.round(ebitda * 3.5); // Revenue TTM (3.5x EBITDA estimate)
      columnValues.numeric_mkvpv3y1 = Math.round(ebitda); // EBITDA TTM
    }
    
    // Enterprise Value Target
    if (valuation.baseValuation || valuation.adjustedValuation) {
      columnValues.numeric_mkvp9005 = valuation.baseValuation || valuation.adjustedValuation;
    }
    
    // Growth Rate (matching backend logic)
    const growthTrend = data.financialInputs?.growthTrend;
    if (growthTrend === 'increasing' || growthTrend === 'fast-growth' || growthTrend === 'rapid-growth') {
      columnValues.numeric_mkvp999e = 15;
    } else if (growthTrend === 'moderate-growth') {
      columnValues.numeric_mkvp999e = 10;
    } else if (growthTrend === 'flat' || growthTrend === 'slow-growth') {
      columnValues.numeric_mkvp999e = 5;
    } else if (growthTrend === 'declining' || growthTrend === 'decreasing') {
      columnValues.numeric_mkvp999e = -5;
    }
    
    // Business Model and Overview
    if (data.businessAnalysis?.businessModel || data.businessAnalysis?.overview) {
      columnValues.long_text_mkvpefns = data.businessAnalysis.businessModel || data.businessAnalysis.overview;
    }
    
    // Extract strengths and risks from aspects (if available)
    const strengths = [];
    const risks = [];
    if (data.businessAnalysis?.aspects) {
      data.businessAnalysis.aspects.forEach(aspect => {
        if (aspect.type === 'positive') {
          strengths.push(`${aspect.title}: ${aspect.description}`);
        } else if (aspect.type === 'negative') {
          risks.push(`${aspect.title}: ${aspect.description}`);
        }
      });
    }
    
    if (strengths.length > 0) {
      columnValues.long_text_mkvp9mxk = strengths.join('\n\n');
    }
    if (risks.length > 0) {
      columnValues.long_text_mkvpsxr4 = risks.join('\n\n');
    }
    
    // Build Notes section (matching backend format)
    const notes = [
      `📊 Valuation Completed: ${new Date().toLocaleDateString()}`,
      `Valuation ID: ${data.valuationId}`,
      '',
      `🔍 ACTION REQUIRED: Research contact information for ${data.website}`,
      '',
      `💰 Valuation Range:`,
      `• Base: $${valuation.baseValuation?.toLocaleString() || valuation.adjustedValuation?.toLocaleString() || 'N/A'}`,
      `• Conservative: $${valuation.conservativeValue?.toLocaleString() || 'N/A'}`,
      `• Optimistic: $${valuation.optimisticValue?.toLocaleString() || 'N/A'}`,
      '',
      `📈 Financial Metrics:`,
      `• Pre-Tax Profit: $${data.financialInputs?.preTaxProfit?.toLocaleString() || 'N/A'}`,
      `• Recurring Revenue: ${data.financialInputs?.recurringRevenue || 0}%`,
      `• Growth Trend: ${data.financialInputs?.growthTrend || 'N/A'}`,
      `• Multiple: ${valuation.multiple ? valuation.multiple.toFixed(2) : 'N/A'}x`,
      '',
      `🎯 Lead Score: ${leadScore}/100`,
      `📍 Source: ${data.utm_source || 'Direct'} / ${data.utm_medium || 'Organic'}`,
      data.businessAnalysis?.overview ? `\n📝 Overview: ${data.businessAnalysis.overview}` : ''
    ].join('\n');
    
    columnValues.long_text_mkvp5e9r = notes;
    
    // Set Next Task (matching backend)
    columnValues.text_mkvpqhnk = 'Research owner contact info and reach out';
    const nextWeek = new Date();
    nextWeek.setDate(nextWeek.getDate() + 7);
    columnValues.date_mkvpfcs0 = { date: nextWeek.toISOString().split('T')[0] };
    
    return {
      itemName: `${companyName} - Free Valuation`,
      columnValues: columnValues
    };
  }
  
  /**
   * REMOVED - Not used in simplified backend-matching version
   */
  formatComprehensiveNotes_REMOVED(data, leadScore, nextSteps) {
    const valuation = data.valuation || {};
    const analysis = data.businessAnalysis || {};
    const financials = data.financialInputs || {};
    
    const notes = {
      '📊 VALUATION RESULTS': {
        'Base Case': this.formatCurrency(valuation.baseCase || valuation.baseValuation),
        'Conservative': this.formatCurrency(valuation.conservative || valuation.conservativeValue),
        'Optimistic': this.formatCurrency(valuation.optimistic || valuation.optimisticValue),
        'EBITDA Multiple': valuation.multiple || 'N/A',
        'Adjusted EBITDA': this.formatCurrency(valuation.ebitda)
      },
      '💰 FINANCIAL INPUTS': {
        'Pre-Tax Profit': this.formatCurrency(financials.preTaxProfit),
        'Growth Trend': financials.growthTrend,
        'Recurring Revenue': `${financials.recurringRevenue || 0}%`
      },
      '🏢 BUSINESS ANALYSIS': {
        'Industry': analysis.industry,
        'Overview': analysis.overview,
        'Business Model': analysis.businessModel,
        'Website Title': analysis.websiteData?.title,
        'Meta Description': analysis.websiteData?.metaDescription
      },
      '🎯 LEAD SCORING': {
        'Lead Score': `${leadScore}/100`,
        'Lead Type': 'Free Valuation',
        'Valuation ID': data.valuationId,
        'Timestamp': data.timestamp
      },
      '📈 MARKETING ATTRIBUTION': {
        'UTM Source': data.utm_source || 'direct',
        'UTM Medium': data.utm_medium || 'organic',
        'UTM Campaign': data.utm_campaign || 'none',
        'Referrer': data.referrer || 'direct',
        'User Agent': data.userAgent
      },
      '🎬 RECOMMENDED ACTIONS': nextSteps.recommendations,
      '📝 BUSINESS ASPECTS': analysis.aspects?.map(a => `${a.type}: ${a.title} - ${a.description}`).join('\n')
    };
    
    return JSON.stringify(notes, null, 2);
  }
  
  /**
   * Calculate lead score (matching backend logic exactly)
   */
  calculateLeadScore(valuation, financialInputs, isPaid = false) {
    let score = 0;
    
    // Base score for completing valuation
    score += 10;
    
    // Higher score for paid customers
    if (isPaid) score += 50;
    
    // Score based on business value (using baseValuation or adjustedValuation)
    const baseValue = valuation?.baseValuation || valuation?.adjustedValuation || 0;
    if (baseValue > 1000000) score += 30; // $1M+
    else if (baseValue > 500000) score += 20; // $500K+
    else if (baseValue > 100000) score += 10; // $100K+
    
    // Score based on recurring revenue
    const recurring = financialInputs?.recurringRevenue || 0;
    if (recurring > 80) score += 15;
    else if (recurring > 50) score += 10;
    else if (recurring > 20) score += 5;
    
    // Score based on growth trend (matching backend values)
    const growth = financialInputs?.growthTrend;
    if (growth === 'increasing' || growth === 'fast-growth' || growth === 'rapid-growth') score += 10;
    else if (growth === 'stable' || growth === 'moderate-growth' || growth === 'flat') score += 5;
    
    return Math.min(score, 100); // Cap at 100
  }
  
  /**
   * REMOVED - Not used in simplified backend-matching version
   */
  generateNextSteps_REMOVED(valuation, financialInputs) {
    const baseValue = valuation?.baseCase || valuation?.baseValuation || 0;
    const leadScore = this.calculateLeadScore(valuation, financialInputs);
    
    let nextTask = '';
    let priority = 'Medium';
    let recommendations = [];
    
    if (leadScore >= 70) {
      nextTask = 'Schedule Discovery Call - High Value Lead';
      priority = 'High';
      recommendations = [
        '1. Schedule discovery call within 24 hours',
        '2. Prepare custom valuation presentation',
        '3. Research comparable transactions',
        '4. Assign senior advisor'
      ];
    } else if (leadScore >= 40) {
      nextTask = 'Send Follow-up Email with Resources';
      priority = 'Medium';
      recommendations = [
        '1. Send educational content about M&A process',
        '2. Schedule follow-up in 1 week',
        '3. Add to nurture campaign',
        '4. Monitor engagement'
      ];
    } else {
      nextTask = 'Add to Nurture Campaign';
      priority = 'Low';
      recommendations = [
        '1. Add to quarterly newsletter',
        '2. Send market updates',
        '3. Check in after 3 months',
        '4. Track business growth'
      ];
    }
    
    // Add value-specific recommendations
    if (baseValue > 5000000) {
      recommendations.push('5. ⭐ Premium opportunity - Executive attention required');
    }
    
    return {
      nextTask,
      priority,
      nextTaskDate: this.getNextBusinessDay(priority === 'High' ? 1 : 3),
      recommendations: recommendations.join('\n')
    };
  }
  

  /**
   * Map AI-generated industry to Monday.com dropdown options
   * Available options: Healthcare, Technology, Manufacturing, Consumer Goods, Financial Services, Other
   */
  mapIndustryToDropdown(industry) {
    if (!industry) return 'Other';
    
    const industryLower = industry.toLowerCase();
    
    // Map common industry terms to Monday.com options
    if (industryLower.includes('health') || industryLower.includes('medical') || 
        industryLower.includes('pharma') || industryLower.includes('biotech')) {
      return 'Healthcare';
    }
    
    if (industryLower.includes('tech') || industryLower.includes('software') || 
        industryLower.includes('saas') || industryLower.includes('ai') || 
        industryLower.includes('data') || industryLower.includes('digital') ||
        industryLower.includes('it ') || industryLower.includes('internet')) {
      return 'Technology';
    }
    
    if (industryLower.includes('manufactur') || industryLower.includes('industrial') || 
        industryLower.includes('production') || industryLower.includes('factory')) {
      return 'Manufacturing';
    }
    
    if (industryLower.includes('consumer') || industryLower.includes('retail') || 
        industryLower.includes('food') || industryLower.includes('beverage') ||
        industryLower.includes('apparel') || industryLower.includes('fashion')) {
      return 'Consumer Goods';
    }
    
    if (industryLower.includes('financ') || industryLower.includes('bank') || 
        industryLower.includes('insurance') || industryLower.includes('investment') ||
        industryLower.includes('fintech')) {
      return 'Financial Services';
    }
    
    // Default to Other for unmatched industries
    return 'Other';
  }

  /**
   * Convert growth trend text to numeric percentage
   */
  parseGrowthRate(growthTrend) {
    if (!growthTrend) return 0;
    
    const growthMap = {
      'declining': -10,
      'flat': 0,
      'slow-growth': 5,
      'moderate-growth': 15,
      'fast-growth': 30,
      'rapid-growth': 50
    };
    
    return growthMap[growthTrend] || 0;
  }

  /**
   * Extract company name from website URL
   */
  extractCompanyName(website) {
    if (!website) return 'Unknown Company';
    
    try {
      const url = new URL(website.startsWith('http') ? website : `https://${website}`);
      return url.hostname.replace('www.', '').split('.')[0];
    } catch (e) {
      return website.replace(/^https?:\/\//, '').replace('www.', '').split('.')[0] || 'Unknown Company';
    }
  }

  /**
   * Generate a unique valuation ID
   */
  generateValuationId() {
    return `val_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Extract user contact information from form data or localStorage
   */
  extractUserContact() {
    // Try to get email from various sources
    let email = null;
    let phone = null;

    // Check if email was provided in a form
    const emailInputs = document.querySelectorAll('input[type="email"], input[name*="email"], input[id*="email"]');
    for (const input of emailInputs) {
      if (input.value && input.value.includes('@')) {
        email = input.value;
        break;
      }
    }

    // Check if phone was provided
    const phoneInputs = document.querySelectorAll('input[type="tel"], input[name*="phone"], input[id*="phone"]');
    for (const input of phoneInputs) {
      if (input.value && input.value.length > 5) {
        phone = input.value;
        break;
      }
    }

    // Check localStorage for previously entered contact info
    try {
      const storedContact = localStorage.getItem('amiva_contact_info');
      if (storedContact) {
        const contact = JSON.parse(storedContact);
        email = email || contact.email;
        phone = phone || contact.phone;
      }
    } catch (e) {
      // Ignore localStorage errors
    }

    return { email, phone };
  }

  /**
   * Store contact information for future use
   */
  storeContactInfo(email, phone) {
    try {
      const contactInfo = { email, phone, timestamp: new Date().toISOString() };
      localStorage.setItem('amiva_contact_info', JSON.stringify(contactInfo));
    } catch (e) {
      logger.warn('Could not store contact info:', e.message);
    }
  }

  /**
   * Capture valuation with enhanced data
   */
  async captureValuationWithContact(valuationData, userEmail = null, userPhone = null) {
    // Extract contact info if not provided
    const contact = userEmail || userPhone ? { email: userEmail, phone: userPhone } : this.extractUserContact();
    
    // Store contact info for future use
    if (contact.email || contact.phone) {
      this.storeContactInfo(contact.email, contact.phone);
    }

    // Generate valuation ID if not provided
    const valuationId = valuationData.valuationId || this.generateValuationId();

    const enhancedData = {
      ...valuationData,
      valuationId,
      userEmail: contact.email,
      userPhone: contact.phone
    };

    return await this.captureValuation(enhancedData);
  }

  /**
   * Disable CRM capture (for development)
   */
  disable() {
    this.isEnabled = false;
    logger.log('CRM capture disabled');
  }

  /**
   * Enable CRM capture
   */
  enable() {
    this.isEnabled = true;
    logger.log('CRM capture enabled');
  }
}

// Create singleton instance
const crmCapture = new CRMCapture();

export default crmCapture;
